/**
 * GitHubConnect Component
 * OAuth-style GitHub authentication with Device Flow support
 * Falls back to guided PAT creation when Device Flow unavailable
 */

import { useState, useEffect, useRef } from 'react'
import { Github, Check, X, Loader2, ExternalLink, Copy, AlertCircle, ArrowRight, Shield, KeyRound } from 'lucide-react'
import { getToken, setToken, getUser, isConfigured, requestDeviceCode, pollForToken, isDeviceFlowAvailable } from '@services/github'

// OAuth-style modal states
const STATES = {
  IDLE: 'idle',
  CHECKING: 'checking',
  CONNECTED: 'connected',
  OAUTH_INIT: 'oauth_init',      // Starting OAuth flow
  OAUTH_PENDING: 'oauth_pending', // Waiting for user to authorize
  PAT_GUIDE: 'pat_guide',         // Guiding user to create PAT
  PAT_INPUT: 'pat_input',         // User entering PAT
  CONNECTING: 'connecting',
  ERROR: 'error',
}

export default function GitHubConnect({ onConnect, compact = false }) {
  const [state, setState] = useState(STATES.CHECKING)
  const [user, setUser] = useState(null)
  const [error, setError] = useState(null)
  const [deviceCode, setDeviceCode] = useState(null)
  const [tokenValue, setTokenValue] = useState('')
  const [copied, setCopied] = useState(false)
  const [oauthAvailable, setOauthAvailable] = useState(false)
  const pollRef = useRef(null)
  const popupRef = useRef(null)

  // Check connection and OAuth availability on mount
  useEffect(() => {
    checkConnection()
    checkOAuthAvailability()
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current)
    }
  }, [])

  const checkOAuthAvailability = async () => {
    const available = await isDeviceFlowAvailable()
    setOauthAvailable(available)
  }

  const checkConnection = async () => {
    setState(STATES.CHECKING)
    setError(null)

    if (!isConfigured()) {
      setState(STATES.IDLE)
      return
    }

    try {
      const userData = await getUser()
      setUser(userData)
      setState(STATES.CONNECTED)
      onConnect?.(userData)
    } catch (err) {
      setState(STATES.ERROR)
      setError('Token invalide')
    }
  }

  const startOAuthFlow = async () => {
    setState(STATES.OAUTH_INIT)
    setError(null)

    try {
      const codeData = await requestDeviceCode()

      if (codeData && codeData.user_code) {
        // Full OAuth Device Flow available
        setDeviceCode(codeData)
        setState(STATES.OAUTH_PENDING)

        // Open GitHub in popup
        popupRef.current = window.open(
          codeData.verification_uri,
          'github-oauth',
          'width=600,height=700,left=200,top=100'
        )

        // Start polling for token
        try {
          const token = await pollForToken(codeData.device_code, codeData.interval || 5)
          setToken(token)
          const userData = await getUser()
          setUser(userData)
          setState(STATES.CONNECTED)
          onConnect?.(userData)
          if (popupRef.current) popupRef.current.close()
        } catch (pollError) {
          setState(STATES.ERROR)
          setError(pollError.message)
        }
      } else {
        // OAuth not available, use PAT guide
        setState(STATES.PAT_GUIDE)
      }
    } catch (err) {
      // Fallback to PAT guide
      setState(STATES.PAT_GUIDE)
    }
  }

  const openGitHubTokenPage = () => {
    popupRef.current = window.open(
      'https://github.com/settings/tokens/new?scopes=repo,workflow&description=EMD%20Factory',
      'github-token',
      'width=800,height=800,left=100,top=50'
    )
    setState(STATES.PAT_INPUT)
  }

  const handleTokenSubmit = async () => {
    if (!tokenValue.trim()) return

    setState(STATES.CONNECTING)
    setError(null)

    try {
      setToken(tokenValue.trim())
      const userData = await getUser()
      setUser(userData)
      setState(STATES.CONNECTED)
      setTokenValue('')
      onConnect?.(userData)
      if (popupRef.current) popupRef.current.close()
    } catch (err) {
      setToken('')
      setState(STATES.ERROR)
      setError('Token invalide ou permissions insuffisantes')
    }
  }

  const handleDisconnect = () => {
    setToken('')
    setUser(null)
    setState(STATES.IDLE)
  }

  const copyCode = () => {
    if (deviceCode?.user_code) {
      navigator.clipboard.writeText(deviceCode.user_code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const reset = () => {
    setState(STATES.IDLE)
    setError(null)
    setDeviceCode(null)
    setTokenValue('')
  }

  // Compact mode for onboarding
  if (compact) {
    return (
      <div className="space-y-3">
        {state === STATES.CONNECTED && user ? (
          <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full" />
            <div className="flex-1">
              <div className="text-sm font-medium text-emerald-300">{user.login}</div>
              <div className="text-xs text-emerald-400/70">Connecté</div>
            </div>
            <button
              onClick={handleDisconnect}
              className="text-xs text-slate-400 hover:text-slate-200"
            >
              Déconnecter
            </button>
          </div>
        ) : state === STATES.PAT_INPUT ? (
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                type="password"
                value={tokenValue}
                onChange={(e) => setTokenValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleTokenSubmit()}
                placeholder="Collez votre token ici..."
                className="flex-1 px-3 py-2 bg-slate-800/80 border border-slate-700/50 rounded-lg text-sm focus:outline-none focus:border-primary-500 text-white placeholder-slate-500"
                autoFocus
              />
              <button
                onClick={handleTokenSubmit}
                disabled={state === STATES.CONNECTING || !tokenValue.trim()}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 rounded-lg text-sm font-medium"
              >
                {state === STATES.CONNECTING ? <Loader2 className="w-4 h-4 animate-spin" /> : 'OK'}
              </button>
            </div>
            {error && <p className="text-xs text-red-400">{error}</p>}
          </div>
        ) : state === STATES.OAUTH_PENDING && deviceCode ? (
          <div className="p-4 bg-slate-800/80 border border-slate-700/50 rounded-lg text-center">
            <p className="text-sm text-slate-300 mb-2">Entrez ce code sur GitHub :</p>
            <div
              onClick={copyCode}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 rounded-lg font-mono text-2xl tracking-widest cursor-pointer hover:bg-slate-800 transition-colors"
            >
              {deviceCode.user_code}
              {copied ? <Check className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5 text-slate-400" />}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
              En attente de votre autorisation...
            </p>
          </div>
        ) : (
          <button
            onClick={startOAuthFlow}
            disabled={state === STATES.OAUTH_INIT}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/50 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50"
          >
            {state === STATES.OAUTH_INIT ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Github className="w-5 h-5" />
            )}
            Se connecter avec GitHub
          </button>
        )}
      </div>
    )
  }

  // Full modal experience
  return (
    <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
            <Github className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-medium text-white">GitHub</h3>
            <p className="text-xs text-slate-400">Déploiement automatique</p>
          </div>
        </div>

        {/* Status badge */}
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
          state === STATES.CONNECTED
            ? 'bg-emerald-500/10 text-emerald-400'
            : state === STATES.ERROR
              ? 'bg-red-500/10 text-red-400'
              : 'bg-slate-700/50 text-slate-400'
        }`}>
          {state === STATES.CHECKING && <Loader2 className="w-3 h-3 animate-spin" />}
          {state === STATES.CONNECTED && <Check className="w-3 h-3" />}
          {state === STATES.ERROR && <AlertCircle className="w-3 h-3" />}
          {state === STATES.IDLE && <X className="w-3 h-3" />}
          <span>
            {state === STATES.CHECKING && 'Vérification...'}
            {state === STATES.CONNECTED && 'Connecté'}
            {state === STATES.CONNECTING && 'Connexion...'}
            {state === STATES.IDLE && 'Non connecté'}
            {state === STATES.ERROR && 'Erreur'}
            {state === STATES.OAUTH_PENDING && 'En attente...'}
            {state === STATES.PAT_GUIDE && 'Configuration...'}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {state === STATES.CONNECTED && user ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={user.avatar_url} alt="" className="w-10 h-10 rounded-full" />
              <div>
                <div className="font-medium text-white">{user.name || user.login}</div>
                <div className="text-sm text-slate-400">@{user.login}</div>
              </div>
            </div>
            <button
              onClick={handleDisconnect}
              className="px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all"
            >
              Déconnecter
            </button>
          </div>

        ) : state === STATES.OAUTH_PENDING && deviceCode ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-primary-500/10 flex items-center justify-center mx-auto mb-4">
              <Github className="w-8 h-8 text-primary-400" />
            </div>
            <p className="text-slate-300 mb-4">Entrez ce code sur GitHub :</p>
            <div
              onClick={copyCode}
              className="inline-flex items-center gap-3 px-6 py-4 bg-slate-900 rounded-xl font-mono text-3xl tracking-[0.5em] cursor-pointer hover:bg-slate-800 transition-colors border border-slate-700"
            >
              <span className="text-white">{deviceCode.user_code}</span>
              {copied ? (
                <Check className="w-6 h-6 text-emerald-400" />
              ) : (
                <Copy className="w-6 h-6 text-slate-500 hover:text-white" />
              )}
            </div>
            <div className="mt-6 flex items-center justify-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">En attente de votre autorisation...</span>
            </div>
            <button
              onClick={reset}
              className="mt-4 text-sm text-slate-500 hover:text-slate-300"
            >
              Annuler
            </button>
          </div>

        ) : state === STATES.PAT_GUIDE ? (
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-3">
                <KeyRound className="w-7 h-7 text-amber-400" />
              </div>
              <h4 className="font-medium text-white mb-2">Créer un token d'accès</h4>
              <p className="text-sm text-slate-400">
                GitHub va s'ouvrir. Créez un token avec les permissions repo et workflow.
              </p>
            </div>

            <div className="space-y-2 p-3 bg-slate-900/50 rounded-lg text-sm">
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center text-xs font-bold shrink-0">1</span>
                <span className="text-slate-300">Cliquez sur "Generate new token"</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center text-xs font-bold shrink-0">2</span>
                <span className="text-slate-300">Les permissions sont pré-sélectionnées</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center text-xs font-bold shrink-0">3</span>
                <span className="text-slate-300">Copiez le token et revenez ici</span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={reset}
                className="px-4 py-3 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all"
              >
                Annuler
              </button>
              <button
                onClick={openGitHubTokenPage}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-500 rounded-lg font-medium transition-all"
              >
                Ouvrir GitHub
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          </div>

        ) : state === STATES.PAT_INPUT ? (
          <div className="space-y-4">
            <div className="text-center py-2">
              <p className="text-sm text-slate-300">Collez votre token GitHub :</p>
            </div>

            <div className="flex gap-2">
              <input
                type="password"
                value={tokenValue}
                onChange={(e) => setTokenValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleTokenSubmit()}
                placeholder="ghp_xxxxxxxxxxxx"
                className="flex-1 px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-lg focus:outline-none focus:border-primary-500 text-white placeholder-slate-500 font-mono"
                autoFocus
              />
              <button
                onClick={handleTokenSubmit}
                disabled={state === STATES.CONNECTING || !tokenValue.trim()}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium transition-all"
              >
                {state === STATES.CONNECTING ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <ArrowRight className="w-5 h-5" />
                )}
              </button>
            </div>

            {error && (
              <p className="text-sm text-red-400 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {error}
              </p>
            )}

            <div className="flex items-center justify-between">
              <button
                onClick={() => setState(STATES.PAT_GUIDE)}
                className="text-sm text-slate-500 hover:text-slate-300"
              >
                Retour
              </button>
              <button
                onClick={openGitHubTokenPage}
                className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300"
              >
                <ExternalLink className="w-3 h-3" />
                Rouvrir GitHub
              </button>
            </div>

            <div className="flex items-center gap-2 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
              <Shield className="w-4 h-4 text-emerald-400 shrink-0" />
              <p className="text-xs text-emerald-300/80">
                Votre token reste sur votre appareil et n'est jamais envoyé à nos serveurs.
              </p>
            </div>
          </div>

        ) : state === STATES.ERROR ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-3">
              <AlertCircle className="w-7 h-7 text-red-400" />
            </div>
            <p className="text-red-400 mb-4">{error || 'Une erreur est survenue'}</p>
            <button
              onClick={reset}
              className="px-6 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm font-medium transition-all"
            >
              Réessayer
            </button>
          </div>

        ) : (
          <div className="text-center py-6">
            <p className="text-slate-400 mb-4">
              Connectez GitHub pour déployer vos sites sur GitHub Pages
            </p>
            <button
              onClick={startOAuthFlow}
              disabled={state === STATES.OAUTH_INIT}
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium transition-all disabled:opacity-50"
            >
              {state === STATES.OAUTH_INIT ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Github className="w-5 h-5" />
              )}
              Se connecter avec GitHub
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
